/**
 * cellsToCanvasElements — pure cell → CanvasElement[] mapper.
 *
 * Shared by both renderer entry points:
 *   - loadFromGraphJson(graphJson)  → graphJson.cells (plain saved JSON)
 *   - bindToGraph(graph)            → graph.getCells().map(c => c.attributes)
 *
 * A live dia.Cell's `.attributes` has the same shape as a serialized cell
 * (id / type / position / size / z / attrs / data), so one mapper serves both.
 * Links are skipped (only elements render); elements are returned z-sorted so
 * background (z=1) draws before foreground.
 */

import type { CanvasElement } from './types'

/** Minimal shape we read off a serialized cell or a live cell's `.attributes`. */
export interface CellLike {
  id?: string | number
  type?: string
  position?: { x: number; y: number }
  size?: { width: number; height: number }
  z?: number
  data?: Record<string, unknown>
  attrs?: Record<string, unknown>
}

/**
 * Classify a cell for the (now vestigial) `CanvasElement.type` tag. Rendering no
 * longer branches on it — planElementRender draws every present part — but the
 * field is retained for any external consumer.
 */
export function getCellType(cell: CellLike): 'path' | 'image' | 'text' {
  if (cell.type === 'standard.Image') return 'image'
  const attrs = cell.attrs as { label?: { text?: string } } | undefined
  if (attrs?.label?.text) return 'text'
  return 'path'
}

function isLink(cell: CellLike): boolean {
  return Boolean(cell.type && cell.type.includes('Link'))
}

/**
 * Map a list of cells to z-sorted CanvasElements, skipping links.
 * Pure — does not mutate its input.
 */
export function cellsToCanvasElements(cells: readonly CellLike[]): CanvasElement[] {
  const elements: CanvasElement[] = []

  for (const cell of cells) {
    if (isLink(cell)) continue

    elements.push({
      id: String(cell.id ?? ''),
      type: getCellType(cell),
      position: cell.position || { x: 0, y: 0 },
      size: cell.size || { width: 0, height: 0 },
      z: cell.z || 0,
      data: (cell.data as CanvasElement['data']) || { layer: 'default', entityHandle: '' },
      attrs: (cell.attrs as CanvasElement['attrs']) || {},
    })
  }

  // Stable z-order: low z (background) first.
  elements.sort((a, b) => a.z - b.z)

  return elements
}
